import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  MessageSquare,
  Plus,
  ArrowUpRight,
  ArrowDownRight,
  Eye,
  IndianRupee,
  Briefcase,
  Activity,
} from 'lucide-react';
import * as Progress from '@radix-ui/react-progress';
import * as Tabs from '@radix-ui/react-tabs';
import { motion } from 'framer-motion';
import { tursoAPI } from '../../services/tursoApi';
import { inquiryService } from '../../services/inquiryService';
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  PieChart,
  Pie,
  Cell,
  Legend,
} from 'recharts';


const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.2,
    },
  },
};

const itemVariants = {
  hidden: { y: 20, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: {
      type: "spring",
      stiffness: 100,
      damping: 15,
    },
  },
};

const StatCard = ({ icon: Icon, label, value, trend, trendValue, onClick }) => (
  <motion.div
    variants={itemVariants}
    onClick={onClick}
    className={`bg-white dark:bg-gray-900 p-6 rounded-xl shadow-md border border-gray-200 dark:border-gray-700 transition-all duration-200
      ${onClick ? 'cursor-pointer hover:shadow-lg hover:border-primary-200 dark:hover:border-accent' : ''}`}
  >
    <div className="flex items-center justify-between mb-4">
      <div className="flex items-center">
        <div className="p-3 bg-primary-100 dark:bg-primary-800 rounded-full flex items-center justify-center">
          <Icon className="h-6 w-6 text-accent dark:text-primary-200" />
        </div>
        <h3 className="ml-4 text-gray-600 dark:text-gray-300 font-medium text-lg">{label}</h3>
      </div>
      {trend && (
        <span
          className={`flex items-center text-sm font-semibold px-2 py-1 rounded-full ${
            trend === 'up'
              ? 'bg-green-100 text-green-700 dark:bg-green-800 dark:text-green-200'
              : 'bg-red-100 text-red-700 dark:bg-red-800 dark:text-red-200'
          }`}
        >
          {trend === 'up' ? (
            <ArrowUpRight className="h-4 w-4 mr-1" />
          ) : (
            <ArrowDownRight className="h-4 w-4 mr-1" />
          )}
          {trendValue}
        </span>
      )}
    </div>
    <p className="mt-2 text-4xl font-extrabold text-gray-900 dark:text-white">{value}</p>
  </motion.div>
);

// RecentActivity Component
const RecentActivity = ({ activities }) => (
  <motion.div
    variants={itemVariants}
    className="bg-white dark:bg-gray-900 rounded-xl shadow-md border border-gray-200 dark:border-gray-700 p-6"
  >
    <div className="flex items-center justify-between mb-6">
      <h3 className="text-xl font-bold text-gray-900 dark:text-white">Recent Activity</h3>
      <Link
        to="/aadashmin/inquiries"
        className="text-sm font-semibold text-accent hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 transition-colors"
      >
        View all &rarr;
      </Link>
    </div>
    <div className="space-y-5">
      {activities.length > 0 ? (
        activities.map((activity, index) => (
          <motion.div
            key={index}
            variants={itemVariants}
            className="flex items-start group"
          >
            <div className="p-2 bg-primary-50 dark:bg-primary-900 rounded-lg flex-shrink-0">
              <Activity className="h-5 w-5 text-accent dark:text-primary-300" />
            </div>
            <div className="ml-4 flex-grow">
              <p className="text-base font-semibold text-gray-800 dark:text-gray-200 group-hover:text-accent dark:group-hover:text-primary-400 transition-colors">{activity.title}</p>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-0.5">{activity.description}</p>
              <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">{activity.time}</p>
            </div>
          </motion.div>
        ))
      ) : (
        <p className="text-gray-500 dark:text-gray-400 text-center">No recent activity.</p>
      )}
    </div>
  </motion.div>
);

const PropertyPerformance = ({ properties }) => (
  <motion.div
    variants={itemVariants}
    className="bg-white dark:bg-gray-900 rounded-xl shadow-md border border-gray-200 dark:border-gray-700 p-6"
  >
    <div className="flex items-center justify-between mb-6">
      <h3 className="text-xl font-bold text-gray-900 dark:text-white">Top Performing Properties</h3>
      <Link
        to="/aadashmin/properties"
        className="text-sm font-semibold text-accent hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 transition-colors"
      >
        View all &rarr;
      </Link>
    </div>
    <div className="space-y-6">
      {properties.map((property, index) => (
        <motion.div
          key={index}
          variants={itemVariants}
          className="space-y-2"
        >
          <div className="flex items-center justify-between text-base">
            <span className="font-semibold text-gray-800 dark:text-gray-200">{property.name}</span>
            <span className="text-gray-600 dark:text-gray-400">{property.views} views</span>
          </div>
          <Progress.Root
            className="h-2.5 w-full bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden relative"
            value={property.progress}
          >
            <Progress.Indicator
              className="h-full bg-accent transition-all duration-500 ease-in-out rounded-full"
              style={{ width: `${property.progress}%` }}
            />
          </Progress.Root>
        </motion.div>
      ))}
    </div>
  </motion.div>
);

const AnalyticsKpiCard = ({ icon: Icon, label, value, trendType, trendValue, trendDescription, iconBg, iconColor, borderColor }) => {
  const trendClass =
    trendType === 'increase'
      ? 'bg-green-100 text-green-700 dark:bg-green-800 dark:text-green-200'
      : trendType === 'decrease'
      ? 'bg-red-100 text-red-700 dark:bg-red-800 dark:text-red-200'
      : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300';

  const iconBgClass = iconBg || 'bg-blue-100 dark:bg-blue-800';
  const iconColorClass = iconColor || 'text-blue-600 dark:text-blue-200';
  const borderClass = borderColor || 'border-blue-500';

  return (
    <motion.div
      variants={itemVariants}
      className={`bg-white dark:bg-gray-900 rounded-xl shadow-md border-t-4 p-6 h-full ${borderClass}`}
    >
      <div className="flex items-start justify-start">
        <div className={`p-3 rounded-full flex items-center justify-center ${iconBgClass}`}>
          <Icon className={`h-6 w-6 ${iconColorClass}`} />
        </div>
        <h3 className="ml-3 text-lg font-medium text-gray-600 dark:text-gray-300">{label}</h3>
      </div>
      <p className="mt-4 text-4xl font-extrabold text-gray-900 dark:text-white">{value}</p>
      <div className="flex items-center mt-2 gap-2">
        {trendValue && (
          <span className={`flex items-center text-sm font-semibold px-2 py-1 rounded-full ${trendClass}`}>
            {trendType === 'increase' ? (
              <ArrowUpRight className="h-4 w-4 mr-1" />
            ) : trendType === 'decrease' ? (
              <ArrowDownRight className="h-4 w-4 mr-1" />
            ) : null}
            {trendValue}
          </span>
        )}
        {trendDescription && <p className="text-sm text-gray-600 dark:text-gray-400">{trendDescription}</p>}
      </div>
    </motion.div>
  );
};


const Dashboard = () => {
  const [selectedTab, setSelectedTab] = useState('overview');
  const [totalViews, setTotalViews] = useState(0);
  const [topProperties, setTopProperties] = useState([]);
  const [totalPropertiesCount, setTotalPropertiesCount] = useState(0);
  const [newInquiriesCount, setNewInquiriesCount] = useState(0);
  const [totalInquiriesCount, setTotalInquiriesCount] = useState(0);
  const [conversionRate, setConversionRate] = useState(0);
  const [recentActivities, setRecentActivities] = useState([]);
  const [propertyViewsOverTimeData, setPropertyViewsOverTimeData] = useState([]);
  const [propertyTypeCounts, setPropertyTypeCounts] = useState([]);


  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        // Fetch total views
        const views = await tursoAPI.getTotalViews();
        setTotalViews(views);

        // Fetch properties for top performing properties and total property count
        const { properties } = await tursoAPI.getAllProperties();
        setTotalPropertiesCount(properties.length);

        // Calculate property type distribution
        const typeCounts = properties.reduce((acc, property) => {
          const type = property.propertyType || 'Other';
          acc[type] = (acc[type] || 0) + 1;
          return acc;
        }, {});

        const colors = ['#3b82f6', '#14b8a6', '#f59e0b', '#ef4444', '#6366f1', '#a855f7', '#ec4899', '#f97316'];
        const distributionData = Object.keys(typeCounts).map((type, index) => ({
          name: type,
          value: typeCounts[type],
          color: colors[index % colors.length],
        }));
        setPropertyTypeCounts(distributionData);

        const sortedProperties = properties.sort((a, b) => b.views - a.views);
        const top4Properties = sortedProperties.slice(0, 4);

        // Calculate progress for top properties
        const maxViews = top4Properties.length > 0 ? top4Properties[0].views : 1;
        const propertiesWithProgress = top4Properties.map(p => ({
          name: p.title,
          views: p.views,
          progress: (p.views / maxViews) * 100,
        }));
        setTopProperties(propertiesWithProgress);

        // Fetch inquiry stats
        const inquiryStats = await inquiryService.getOverallInquiryStats();
        setNewInquiriesCount(inquiryStats.newInquiriesCount);
        setTotalInquiriesCount(inquiryStats.totalInquiriesCount);

        if (views > 0) {
          const calculatedConversionRate = (inquiryStats.totalInquiriesCount / views) * 100;
          setConversionRate(calculatedConversionRate);
        } else {
          setConversionRate(0);
        }

        // Fetch all inquiries for recent activity
        const allInquiries = await inquiryService.getAllInquiries();

        // Combine properties and inquiries for recent activity
        const combinedActivities = [];

        properties.forEach(property => {
          // Add new property activity
          combinedActivities.push({
            title: `New Property Listed: ${property.title}`,
            description: `${property.propertyType} in ${property.location.city} has been added to the listings`,
            time: new Date(property.createdAt).toLocaleString(),
            timestamp: new Date(property.createdAt).getTime(),
          });
          if (property.updatedAt && property.updatedAt !== property.createdAt) {
            combinedActivities.push({
              title: `Property Updated: ${property.title}`,
              description: `Details updated for ${property.propertyType} in ${property.location.city}`,
              time: new Date(property.updatedAt).toLocaleString(),
            timestamp: new Date(property.updatedAt).getTime(),
            });
          }
        });

        allInquiries.forEach(inquiry => {
          combinedActivities.push({
            title: `New Inquiry: ${inquiry.name}`,
            description: `${inquiry.name} inquired about a property. Type: ${inquiry.type}`,
            time: new Date(inquiry.createdAt).toLocaleString(),
            timestamp: new Date(inquiry.createdAt).getTime(),
          });
        });

        const sortedActivities = combinedActivities.sort((a, b) => b.timestamp - a.timestamp).slice(0, 5);
        setRecentActivities(sortedActivities);

        const today = new Date();
        const thirtyDaysAgo = new Date();
        thirtyDaysAgo.setDate(today.getDate() - 29);

        const startDate = thirtyDaysAgo.toISOString().split('T')[0];
        const endDate = today.toISOString().split('T')[0];

        const dailyViews = await tursoAPI.getTotalDailyViews(startDate, endDate);

        const chartData = [];
        let currentDate = new Date(thirtyDaysAgo);
        while (currentDate <= today) {
          const dateString = currentDate.toISOString().split('T')[0];
          const found = dailyViews.find(d => d.date === dateString);
          chartData.push({
            date: dateString,
            Views: found ? found.Views : 0,
          });
          currentDate.setDate(currentDate.getDate() + 1);
        }
        setPropertyViewsOverTimeData(chartData);


      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      }
    };

    fetchDashboardData();
  }, []);

  const stats = [
    { icon: Briefcase, label: 'Total Properties', value: totalPropertiesCount.toLocaleString(), trend: 'up', trendValue: '12%' },
    { icon: MessageSquare, label: 'New Inquiries', value: newInquiriesCount.toLocaleString(), trend: 'up', trendValue: '8%' },
    { icon: Eye, label: 'Total Views', value: totalViews.toLocaleString(), trend: 'up', trendValue: '24%' },
    { icon: IndianRupee, label: 'Estimated Revenue', value: '₹84.5K', trend: 'down', trendValue: '3%' }
  ];

  const currencyFormatter = (number) =>
    `₹${Intl.NumberFormat('en-IN', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(number)}`;

  const CustomRechartsTooltip = ({ active, payload, label, unit = '' }) => {
    if (active && payload && payload.length) {
      return (
        <div className="p-3 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 text-sm">
          <p className="font-semibold text-gray-800 dark:text-gray-200 mb-1">{label}</p>
          {payload.map((entry, index) => (
            <p key={`item-${index}`} className="text-gray-700 dark:text-gray-300" style={{ color: entry.color }}>
              {`${entry.name}: ${entry.value.toLocaleString()}${unit}`}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };


  const PropertyTypesPieChartTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      const totalProperties = propertyTypeCounts.reduce((sum, item) => sum + item.value, 0);
      const percentage = (totalProperties > 0 ? (data.value / totalProperties) * 100 : 0).toFixed(1);

      return (
        <div className="w-96 rounded-lg text-sm bg-white dark:bg-gray-800 p-3 shadow-lg border border-gray-200 dark:border-gray-700">
          <div className="flex flex-1 space-x-2.5">
            <div className={`flex w-1.5 flex-col bg-[${data.color}] rounded`} />
            <div className="w-full">
              <p className="text-gray-800 dark:text-gray-200 font-semibold mb-1">
                {data.name}
              </p>
              <p className="font-medium text-gray-700 dark:text-gray-300">
                {data.value} properties
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {percentage}% of Total
              </p>
            </div>
          </div>
        </div>
      );
    }
    return null;
  };

  const customYAxisTicks = [1, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100];
  const maxViewsInFetchedData = propertyViewsOverTimeData.reduce((max, item) => Math.max(max, item.Views), 0);
  const domainMax = Math.max(...customYAxisTicks, maxViewsInFetchedData);

  return (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={containerVariants}
      className="space-y-8 p-2 md:p-6 lg:p-8 bg-gray-50 dark:bg-gray-950 min-h-screen"
    >

      {/* Header and Add Property Button Section */}
      <motion.div
        variants={itemVariants}
        className="flex flex-col sm:flex-row items-start sm:items-center justify-between dark:bg-gray-950 gap-4"
      >
        <div className="dark:bg-gray-950">
          <h1 className="text-3xl font-extrabold  text-gray-900 dark:text-white">Dashboard Overview</h1>
          <p className="mt-1 text-md text-gray-600 dark:text-gray-400">
            Welcome back! Here's what's happening with your real estate portfolio today.
          </p>
        </div>
        <Link
          to="/aadashmin/properties/new"
          className="inline-flex items-center px-5 py-2.5 bg-accent text-white text-base font-semibold rounded-lg shadow-md
                         hover:bg-primary-700 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
        >
          <Plus className="h-5 w-5 mr-2" />
          Add New Property
        </Link>
      </motion.div>

      {/* Main Stat Cards Grid Section */}
      <motion.div
        initial="hidden"
        animate="visible"
        variants={containerVariants}
        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6"
      >
        {stats.map((stat, index) => (
          <StatCard key={index} {...stat} />
        ))}
      </motion.div>

      {/* Tabs for Overview and Analytics Section */}
      <motion.div
        variants={itemVariants}
      >
        <Tabs.Root defaultValue="overview" className="w-full">
          {/* Removed px-4 for small screens, kept sm:px-6 for medium and larger */}
          <Tabs.List className="flex border-b border-gray-200 dark:border-gray-700 px-0 sm:px-6 pt-2">
            <Tabs.Trigger
              value="overview"
              className="px-5 py-3 text-base font-semibold text-gray-700 dark:text-gray-300 border-b-2 border-transparent hover:border-gray-300 dark:hover:border-gray-600
                                 data-[state=active]:border-accent data-[state=active]:text-accent dark:data-[state=active]:text-primary-400
                                 transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2"
              onClick={() => setSelectedTab('overview')}
            >
              Overview
            </Tabs.Trigger>
            <Tabs.Trigger
              value="analytics"
              className="px-5 py-3 text-base font-semibold text-gray-700 dark:text-gray-300 border-b-2 border-transparent hover:border-gray-300 dark:hover:border-gray-600
                                 data-[state=active]:border-accent data-[state=active]:text-accent dark:data-[state=active]:text-primary-400
                                 transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2"
              onClick={() => setSelectedTab('analytics')}
            >
              Analytics
            </Tabs.Trigger>
          </Tabs.List>

          {/* Changed p-4 to px-0 py-4 for small screens, kept sm:p-6 for medium and larger */}
          <Tabs.Content value="overview" className="px-0 py-4 sm:p-6">
            <motion.div
              key="overview-content"
              initial="hidden"
              animate="visible"
              variants={containerVariants}
              className="grid grid-cols-1 lg:grid-cols-2 gap-6"
            >
              {/* RecentActivity and PropertyPerformance components already have their own card styling */}
              <RecentActivity activities={recentActivities} />
              <PropertyPerformance properties={topProperties} />
            </motion.div>
          </Tabs.Content>

          {/* Changed p-4 to px-0 py-4 for small screens, kept sm:p-6 for medium and larger */}
          <Tabs.Content value="analytics" className="px-0 py-4 sm:p-6">
            <motion.div
              key="analytics-content"
              initial="hidden"
              animate="visible"
              variants={containerVariants}
              className="space-y-8"
            >

              {/* AnalyticsKpiCards would go here if you decide to add them back */}
              <motion.div
                initial="hidden"
                animate="visible"
                variants={containerVariants}
                className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6"
              >
                {/* <AnalyticsKpiCard ... /> */}
              </motion.div>

              <motion.div
                initial="hidden"
                animate="visible"
                variants={containerVariants}
                className="grid grid-cols-1 lg:grid-cols-3 gap-6"
              >
                {/* Property Views Over Time Chart */}
                <motion.div variants={itemVariants} className="lg:col-span-2 bg-white dark:bg-gray-900 rounded-xl shadow-md border border-gray-200 dark:border-gray-700 p-4 sm:p-6">
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">Property Views Over Time</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">Overall website traffic and views on property listings.</p>
                  <ResponsiveContainer width="100%" height={300}>
                    <AreaChart
                      data={propertyViewsOverTimeData}
                      margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" className="dark:stroke-gray-700" vertical={false} />
                      <XAxis
                        dataKey="date"
                        tickLine={false}
                        axisLine={false}
                        tickFormatter={(str) => {
                          const date = new Date(str);
                          const isFirstOfMonth = date.getDate() === 1;
                          const isEveryNthDay = date.getDate() % 7 === 0;
                          const isFirstOrLast = str === propertyViewsOverTimeData[0].date || str === propertyViewsOverTimeData[propertyViewsOverTimeData.length - 1].date;

                          if (isFirstOfMonth || isEveryNthDay || isFirstOrLast) {
                            return `${date.getDate()}/${date.getMonth() + 1}`;
                          }
                          return '';
                        }}
                        style={{ fontSize: '12px', fontFamily: 'Inter, sans-serif', fill: '#6b7280', color: '#6b7280' }}
                        className="dark:text-gray-400"
                      />
                      <YAxis
                        tickFormatter={(value) => `${value.toLocaleString()}`}
                        tickLine={false}
                        axisLine={false}
                        domain={[0, domainMax]}
                        ticks={customYAxisTicks}
                        allowDecimals={false}
                        style={{ fontSize: '12px', fontFamily: 'Inter, sans-serif', fill: '#6b7280', color: '#6b7280' }}
                        className="dark:text-gray-400"
                      />
                      <Tooltip content={<CustomRechartsTooltip unit=" views" />} cursor={{ stroke: '#cbd5e1', strokeWidth: 1, className: 'dark:stroke-gray-600' }} />
                      <Area
                        type="monotone"
                        dataKey="Views"
                        stroke="#8B5CF6"
                        fillOpacity={1}
                        fill="url(#colorViewsGradient)"
                        strokeWidth={2}
                        activeDot={{ r: 6, fill: '#8B5CF6', stroke: '#fff', strokeWidth: 2 }}
                      />
                      <defs>
                        <linearGradient id="colorViewsGradient" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#8B5CF6" stopOpacity={0.4} />
                          <stop offset="95%" stopColor="#8B5CF6" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                    </AreaChart>
                  </ResponsiveContainer>
                </motion.div>


                <motion.div variants={itemVariants} className="lg:col-span-1 bg-white dark:bg-gray-900 rounded-xl shadow-md border border-gray-200 dark:border-gray-700  sm:p-0 py-10">
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2 mt-2 ml-2">Property Types Distribution</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-4 ml-2">Breakdown of properties by type in your portfolio.</p>
                  <ResponsiveContainer width="100%" height={280}>
                    <PieChart>
                      <Pie
                        data={propertyTypeCounts}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={90}
                        fill="#8884d8"
                        paddingAngle={5}
                        dataKey="value"
                        nameKey="name"
                        labelLine={false}
                      >
                        {propertyTypeCounts.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip content={<PropertyTypesPieChartTooltip />} />
                      <Legend
                        verticalAlign="bottom"
                        align="center"
                        height={36}
                        iconType="circle"
                        formatter={(value) => (
                          <span className="text-gray-700 dark:text-gray-300">{value}</span>
                        )}
                      />
                      <text
                        x="50%"
                        y="45%"
                        textAnchor="middle"
                        dominantBaseline="middle"
                        className="font-bold text-xl"
                        style={{ fill: 'var(--text-color-dark-mode, #FFFFFF)' }}
                      >
                        {totalPropertiesCount}
                      </text>
                      <text
                        x="50%"
                        y="55%"
                        textAnchor="middle"
                        dominantBaseline="middle"
                        className="text-sm"
                        style={{ fill: 'var(--text-color-dark-mode-secondary, #FFFFFF)' }}
                      >
                        Total Properties
                      </text>
                    </PieChart>
                  </ResponsiveContainer>
                </motion.div>
              </motion.div>
            </motion.div>
          </Tabs.Content>
        </Tabs.Root>
      </motion.div>

    </motion.div>
  );
};

export default Dashboard;